import React from "react";
import { Box, Button, Stack, IconButton, Drawer } from "@mui/material";
import Badge from "@mui/material/Badge";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link, useHistory } from "react-router-dom";
import { CartItem } from "../../../lib/types/search";
import { Messages, serverApi } from "../../../lib/config";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import Swal from "sweetalert2";
import { useGlobals } from "../../hooks/useGlobals";
import OrderService from "../../services/OrderService";

interface BasketProps {
  cartItems: CartItem[];
  onAdd: (item: CartItem) => void;
  onRemove: (item: CartItem) => void;
  onDelete: (item: CartItem) => void;
  onDeleteAll: () => void;
}

export default function Basket(props: BasketProps) {
  const { cartItems, onAdd, onRemove, onDelete, onDeleteAll } = props;
  const { authMember, setOrderBuilder } = useGlobals();
  const history = useHistory();
  const subtotal = cartItems.reduce((a, c) => a + c.quantity * c.price, 0);
  const freeShippingThreshold = 100;
  const shippingCost = subtotal >= freeShippingThreshold ? 0 : 5;
  const totalPrice = (subtotal + shippingCost).toFixed(2);

  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const proceedOrderHandler = async () => {
    try {
      handleClose();
      if (!authMember) throw new Error(Messages.error2);
      const order = new OrderService();
      await order.createOrder(cartItems);
      onDeleteAll();
      setOrderBuilder(new Date());
      history.push("/orders");
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  const handleClearBasket = async () => {
    const result = await Swal.fire({
      icon: "error",
      title: "Are you sure?",
      text: "Are you sure to clear all?",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#2563eb",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      onDeleteAll();
    }
  };

  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Box className="hover-line">
      <IconButton
        aria-label="cart"
        onClick={handleClick}
      >
        <Badge
          badgeContent={itemCount}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#1773cf",
              color: "#ffffff",
            },
          }}
        >
          <img src="/icons/shopping-cart.svg" alt="Cart" />
        </Badge>
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        className="basket-drawer"
        ModalProps={{
          keepMounted: true,
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0)",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          },
        }}
        PaperProps={{
          className: "basket-drawer-paper",
          sx: {
            width: 380,
            maxWidth: "90vw",
            top: 70,
            height: "calc(100vh - 70px)",
            minHeight: "calc(100vh - 70px)",
            maxHeight: "calc(100vh - 70px)",
            borderRadius: 0,
            boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Stack className="basket-frame basket-frame--drawer">
          {/* Header: Your Cart + badge + close + clear basket */}
          <Box className="basket-header">
            <Box className="basket-header-left">
              <ShoppingCartIcon className="basket-header-cart-icon" />
              <span className="basket-header-title">Your Cart</span>
              {itemCount > 0 && (
                <span className="basket-header-badge">{itemCount} {itemCount === 1 ? "Item" : "Items"}</span>
              )}
            </Box>
            <Box className="basket-header-right">
              {cartItems.length > 0 && (
                <IconButton
                  className="basket-clear-btn"
                  onClick={handleClearBasket}
                  size="small"
                  aria-label="Clear basket"
                  title="Clear basket"
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                className="basket-close-btn"
                onClick={handleClose}
                size="small"
                aria-label="Close cart"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Item list */}
          <Box className="basket-list-wrap">
            {cartItems.length === 0 ? (
              <Box className="basket-empty">Cart is empty!</Box>
            ) : (
              <Stack className="basket-list">
                {cartItems.map((item: CartItem) => {
                  const imagePath = `${serverApi}/${item.image}`;
                  return (
                    <Box className="basket-item" key={item._id}>
                      <Link
                        to={`/products/${item._id}`}
                        className="basket-item-main"
                        onClick={handleClose}
                      >
                        <img
                          src={imagePath}
                          alt={item.name}
                          className="basket-item-img"
                        />
                        <Box className="basket-item-content">
                          <span className="basket-item-name">
                            {item.name}
                          </span>
                          <span className="basket-item-price">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </Box>
                      </Link>
                      <Box className="basket-item-actions">
                        <Box className="basket-qty-control">
                          <button
                            type="button"
                            className="basket-qty-btn"
                            onClick={() => onRemove(item)}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="basket-qty-value">{item.quantity}</span>
                          <button
                            type="button"
                            className="basket-qty-btn"
                            onClick={() => onAdd(item)}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </Box>
                        <IconButton
                          className="basket-item-remove"
                          onClick={() => onDelete(item)}
                          size="small"
                          aria-label="Remove item"
                          title="Remove from cart"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Summary + Checkout */}
          {cartItems.length > 0 && (
            <>
              <Box className="basket-summary">
                <Box className="basket-summary-row">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </Box>
                <Box className="basket-summary-row">
                  <span>Shipping</span>
                  <span className={shippingCost === 0 ? "basket-shipping-free" : ""}>
                    {shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}
                  </span>
                </Box>
                <Box className="basket-summary-row basket-summary-total">
                  <span>Total</span>
                  <span>${totalPrice}</span>
                </Box>
              </Box>
              <Button
                className="basket-checkout-btn"
                fullWidth
                variant="contained"
                onClick={proceedOrderHandler}
                endIcon={<ArrowForwardIcon />}
              >
                Proceed to Checkout
              </Button>
              <p className="basket-footer-note">Free shipping on orders of $100 or more.</p>
            </>
          )}
        </Stack>
      </Drawer>
    </Box>
  );
}
